import { useAuth0 } from '@auth0/auth0-react'

import posthog from 'posthog-js'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useHistory, useLocation } from 'react-router-dom'

import { AuthPromptModal } from '../components/AuthPromptModal'
import {
  ReviewGetStartedAssurances,
  ReviewGetStartedDisclaimer,
  ReviewGetStartedIntro,
  ReviewSubmitAnonymityReminder,
} from '../components/ReviewGetStartedTrust'
import { ReviewProgressBar } from '../components/ReviewProgressBar'
import { ReviewRatingField } from '../components/ReviewRatingField'
import { ReviewStepHeader } from '../components/ReviewStepHeader'
import { ReviewWrapUpIntro } from '../components/ReviewWrapUpIntro'
import { ReviewYesNoField } from '../components/ReviewYesNoField'
import { Spinner } from '../components/Spinner'
import { TopLogo } from '../components/TopLogo'
import { FormServiceAddSubmissionAnswerEvent } from '../events/FormServiceAddSubmissionAnswerEvent'
import { FormServiceAddSubmissionEvent } from '../events/FormServiceAddSubmissionEvent'
import { useAppToast } from '../hooks/useAppToast'
import { FormFieldDTO } from '../models/FormFieldDTO.model'
import { FormFieldTypes } from '../models/FormFieldTypes.model'
import { FormPageDTO } from '../models/FormPageDTO.model'
import { FormRelations } from '../models/FormRelations.model'
import { FormSubmissionError } from '../models/FormSubmissionError.model'
import { UserDTO } from '../models/UserDTO.model'
import { AgencyService } from '../services/AgencyService/AgencyService'
import { FormService } from '../services/FormService/FormService'
import { HospitalService } from '../services/HospitalService/HospitalService'
import UserService from '../services/UserService/UserService'
import { scrollIntoView } from '../utils'
import { useCanPerformVerifiedAction, useShouldDeferWriteGate } from '../utils/emailVerification'
import { clearReviewDraft, hasReviewDraftContent, loadReviewDraft, saveReviewDraft } from '../utils/reviewDraftStorage'
import {
  isGetStartedPageComplete,
  isReviewQuestionPageComplete,
  isWouldWorkAgainField,
  isWrapUpVisibleField,
  LAST_WORKED_MONTH_INPUT_NAME,
} from '../utils/reviewFormNavigation'
import { getEntityDetailPathFromReviewType, waitForReviewSuccessNavDelay } from '../utils/reviewSuccessNavigation'

const reviewTypeToFormId = {
  agency: Number(process.env.REACT_APP_AGENCY_REVIEW_FORM_ID) || 35,
  hospital: Number(process.env.REACT_APP_HOSPITAL_REVIEW_FORM_ID) || 34,
}

type FieldValues = Record<string | number, unknown>

type UpdateFieldValueMap = (fieldID: string | number, value: unknown) => void

type ValidationErrors = Record<string, string | JSX.Element>

type FormFieldElement = {
  field: FormFieldDTO
  updateFieldValueMap: UpdateFieldValueMap
  validationErrors: ValidationErrors
}
type FormFieldElementForRestrictedRoles = {
  field: FormFieldDTO
  updateFieldValueMap: UpdateFieldValueMap
  validationErrors: ValidationErrors
  disablePrimaryUnit: boolean
  setDisablePrimaryUnit: React.Dispatch<React.SetStateAction<boolean>>
}
const SectionListItem = ({ field, compact }: FormFieldElement & { compact?: boolean }) => {
  if (compact) {
    return (
      <div className="mb-2 text-center">
        <h2 className="text-lg font-bold text-graphite-grey md:text-xl">{field.label}</h2>
      </div>
    )
  }

  return (
    <div className="mb-8 px-4">
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-center font-bold text-graphite-grey break-words">
        {field.label}
      </h2>
    </div>
  )
}
const TextAreaListItem = ({
  field,
  fieldValue,
  updateFieldValueMap,
  validationErrors,
  isWrapUp,
}: FormFieldElement & { fieldValue?: unknown; isWrapUp?: boolean }) => {
  const [textValue, setTextValue] = useState(() =>
    fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : '',
  )

  useEffect(() => {
    if (fieldValue !== undefined && fieldValue !== null) {
      setTextValue(String(fieldValue))
    }
  }, [fieldValue])

  useEffect(() => {
    updateFieldValueMap(field.id, textValue)
  }, [field.id, textValue])

  const placeholder = isWrapUp
    ? "Add context, examples, or anything the ratings didn't capture."
    : !field.isRequired
      ? 'Write your optional text here...'
      : ''

  return (
    <div id={`review-input-${field.id}`} className="mb-6">
      {!isWrapUp && (
        <label className="block text-sm font-semibold text-graphite-grey mb-2">
          {field.label}
          {!field.isRequired && (
            <span className="ml-2 text-xs font-normal text-gray-500 bg-blue-100 text-transform: uppercase text px-2 py-1 rounded-2xl">
              Optional
            </span>
          )}
        </label>
      )}
      <textarea
        className={`w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white resize-none ${
          isWrapUp ? 'placeholder:text-gray-400' : 'placeholder:italic'
        }`}
        rows={5}
        placeholder={placeholder}
        value={textValue}
        onChange={(evt) => {
          setTextValue(evt.target.value)
        }}
        onFocus={(evt) => {
          scrollIntoView(evt.target)
        }}
      />
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

const TextListItem = ({
  field,
  fieldValue,
  updateFieldValueMap,
  validationErrors,
}: FormFieldElement & { fieldValue?: unknown }) => {
  const [tempTextValue, setTempTextValue] = useState(() =>
    fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : '',
  )

  useEffect(() => {
    if (fieldValue !== undefined && fieldValue !== null) {
      setTempTextValue(String(fieldValue))
    }
  }, [fieldValue])

  useEffect(() => {
    updateFieldValueMap(field.id, tempTextValue)
  }, [field.id, tempTextValue])
  return (
    <div id={`review-input-${field.id}`} className="mb-6">
      <label className="block text-sm font-semibold text-graphite-grey mb-2">{field.label}</label>
      <input
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
        onChange={(evt) => {
          setTempTextValue(evt.target.value)
        }}
        onFocus={(evt) => {
          scrollIntoView(evt.target)
        }}
        value={tempTextValue}
      />
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

const SelectListItem = ({
  field,
  fieldValue,
  updateFieldValueMap,
  userDetails,
  validationErrors,
  disablePrimaryUnit,
  setDisablePrimaryUnit,
}: FormFieldElementForRestrictedRoles & { fieldValue?: unknown; userDetails: UserDTO }) => {
  const { id } = useParams<{ id: string }>()
  const nonRestrictedRoles = ['RN', 'LPN/LVN', 'RT']
  const isPrimaryUnitDropdown = field.label === 'Primary Unit/Specialty'
  const isHealthcareRoleDropdown = field.label === 'Healthcare Role'
  const isWrapupDropdown = field.label === 'Scrub Color' || field.label === 'Charting Software'
  const isOptionalMonthDropdown = field.inputName === LAST_WORKED_MONTH_INPUT_NAME
  const usesEmptyDefault = isWrapupDropdown || isOptionalMonthDropdown
  const getInitialValue = () => {
    if (usesEmptyDefault) {
      return ''
    }
    // Healthcare Role dropdown
    if (isHealthcareRoleDropdown && userDetails.position_title) {
      const userValue = userDetails.position_title.toLowerCase().trim()
      let found = field.choices?.find((c) => c.value?.toLowerCase().trim() === userValue)
      if (!found) {
        found = field.choices?.find((c) => c.label?.toLowerCase().trim() === userValue)
      }
      if (!found) {
        found = field.choices?.find((c) => {
          const choiceValue = c.value?.toLowerCase().trim()
          return choiceValue && userValue.includes(choiceValue)
        })
      }
      if (!found) {
        found = field.choices?.find((c) => {
          const choiceValue = c.value?.toLowerCase().trim()
          const choiceLabel = c.label?.toLowerCase().trim()
          return (choiceValue && choiceValue.includes(userValue)) || (choiceLabel && choiceLabel.includes(userValue))
        })
      }
      if (found) {
        return found.value
      }
      return field.choices?.[0]?.value || ''
    }
    // Primary Unit dropdown
    if (isPrimaryUnitDropdown && userDetails.nursing_specialization) {
      const userValue = userDetails.nursing_specialization.toLowerCase().trim()
      let found = field.choices?.find((c) => c.value?.toLowerCase().trim() === userValue)
      if (!found) {
        found = field.choices?.find((c) => c.label?.toLowerCase().trim() === userValue)
      }
      if (!found) {
        found = field.choices?.find((c) => {
          const choiceValue = c.value?.toLowerCase().trim()
          const choiceLabel = c.label?.toLowerCase().trim()
          return (choiceValue && choiceValue.includes(userValue)) || (choiceLabel && choiceLabel.includes(userValue))
        })
      }
      if (!found) {
        found = field.choices?.find((c) => {
          const choiceValue = c.value?.toLowerCase().trim()
          const choiceLabel = c.label?.toLowerCase().trim()
          return (choiceValue && userValue.includes(choiceValue)) || (choiceLabel && userValue.includes(choiceLabel))
        })
      }
      if (found) {
        return found.value
      }
      return field.choices?.[0]?.value || ''
    }
    if (field.defaultValue === '{user:nursing_specialization}') {
      if (userDetails.nursing_specialization) {
        const found = field.choices?.find((c) => c.value === userDetails.nursing_specialization)
        if (found) return found.value
      }
      return field.choices?.[0]?.value || ''
    }
    if (field.defaultValue === '{user:position_title}') {
      if (userDetails.position_title) {
        const found = field.choices?.find((c) => c.value === userDetails.position_title)
        if (found) return found.value
      }
      return field.choices?.[0]?.value || ''
    }
    if (id && (field.inputName === 'a' || field.inputName === 'h')) {
      return id
    }
    return field.choices?.[0]?.value || ''
  }
  const resolveInitialValue = () => {
    if (fieldValue !== undefined && fieldValue !== null && String(fieldValue) !== '' && String(fieldValue) !== 'null') {
      return String(fieldValue)
    }
    return getInitialValue()
  }
  const [selectedValue, setSelectedValue] = useState(resolveInitialValue)

  useEffect(() => {
    if (fieldValue !== undefined && fieldValue !== null && String(fieldValue) !== '' && String(fieldValue) !== 'null') {
      setSelectedValue(String(fieldValue))
    }
  }, [fieldValue])

  // Update field value when selectedValue changes
  useEffect(() => {
    if (!isPrimaryUnitDropdown || !disablePrimaryUnit) {
      if (usesEmptyDefault && selectedValue === '') {
        updateFieldValueMap(field.id, null)
      } else {
        updateFieldValueMap(field.id, selectedValue)
      }
    } else {
      updateFieldValueMap(field.id, null)
    }
  }, [selectedValue, disablePrimaryUnit])
  useEffect(() => {
    if (isHealthcareRoleDropdown) {
      const shouldDisable = !nonRestrictedRoles.includes(selectedValue)
      setDisablePrimaryUnit?.(shouldDisable)
    }
  }, [selectedValue])
  useEffect(() => {
    if (isPrimaryUnitDropdown) {
      if (disablePrimaryUnit) {
        setSelectedValue('')
      } else {
        const newValue = getInitialValue()
        setSelectedValue(newValue)
      }
    }
  }, [disablePrimaryUnit])
  if (isPrimaryUnitDropdown && disablePrimaryUnit) {
    return null
  }
  const choices = isOptionalMonthDropdown
    ? field.choices
    : field.choices?.sort((a, b) => a.label.localeCompare(b.label))
  return (
    <div
      id={`review-input-${field.id}`}
      className={`mb-6 ${id && (field.inputName === 'a' || field.inputName === 'h') ? 'hidden' : ''}`}
    >
      <label className="block text-sm font-semibold text-graphite-grey mb-2">{field.label}</label>
      <select
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
        value={selectedValue || ''}
        onChange={(evt) => setSelectedValue(evt.target.value)}
      >
        {isWrapupDropdown && <option value="">-</option>}
        {isOptionalMonthDropdown && (
          <option value="" disabled>
            Select month
          </option>
        )}
        {choices?.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

const NumberListItem = ({
  field,
  fieldValue,
  updateFieldValueMap,
  validationErrors,
}: FormFieldElement & { fieldValue?: unknown }) => {
  const [numberValue, setNumberValue] = useState(() =>
    fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : '',
  )

  useEffect(() => {
    if (fieldValue !== undefined && fieldValue !== null) {
      setNumberValue(String(fieldValue))
    }
  }, [fieldValue])

  return (
    <div id={`review-input-${field.id}`} className="mb-6">
      <label className="block text-sm font-semibold text-graphite-grey mb-2">{field.label}</label>
      <input
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
        type="number"
        value={numberValue}
        onChange={(evt) => {
          setNumberValue(evt.target.value)
          updateFieldValueMap(field.id, evt.target.value)
        }}
      />
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

type ReviewType = 'hospital' | 'agency'
// type SetFieldValues = React.Dispatch<React.SetStateAction<any>>
const NodeListItem = (
  field: FormFieldDTO,
  updateFieldValueMap: UpdateFieldValueMap,
  userDetails: UserDTO,
  validationErrors: ValidationErrors,
  disablePrimaryUnit: boolean,
  setDisablePrimaryUnit: React.Dispatch<React.SetStateAction<boolean>>,
  options?: {
    compactLabel?: boolean
    fieldValue?: unknown
    isRatingPage?: boolean
    isWrapUp?: boolean
    onRatingSelected?: (fieldId: number, value: number) => void
  },
): JSX.Element => {
  const { compactLabel, fieldValue, isRatingPage, isWrapUp, onRatingSelected } = options ?? {}

  switch (field.type) {
    case FormFieldTypes.section:
      return (
        <SectionListItem
          updateFieldValueMap={updateFieldValueMap}
          key={field.id}
          field={field}
          validationErrors={validationErrors}
          compact={isRatingPage}
        />
      )
    case FormFieldTypes.number:
      return (
        <NumberListItem
          updateFieldValueMap={updateFieldValueMap}
          key={field.id}
          field={field}
          fieldValue={fieldValue}
          validationErrors={validationErrors}
        />
      )
    case FormFieldTypes.select:
      return (
        <SelectListItem
          updateFieldValueMap={updateFieldValueMap}
          key={field.id}
          field={field}
          fieldValue={fieldValue}
          userDetails={userDetails}
          validationErrors={validationErrors}
          disablePrimaryUnit={disablePrimaryUnit}
          setDisablePrimaryUnit={setDisablePrimaryUnit}
        />
      )
    case FormFieldTypes.text:
      return (
        <TextListItem
          updateFieldValueMap={updateFieldValueMap}
          key={field.id}
          field={field}
          fieldValue={fieldValue}
          validationErrors={validationErrors}
        />
      )
    case FormFieldTypes.radio:
      if (!field.isRating) {
        return (
          <ReviewYesNoField
            key={field.id}
            field={field}
            fieldValue={fieldValue}
            onChange={updateFieldValueMap}
            validationError={validationErrors[`field_${field.id}`]}
            compactLabel={compactLabel}
          />
        )
      }
      return (
        <ReviewRatingField
          key={field.id}
          field={field}
          fieldValue={fieldValue}
          onChange={updateFieldValueMap}
          onRatingSelected={onRatingSelected}
          validationError={validationErrors[`field_${field.id}`]}
        />
      )
    case FormFieldTypes.textarea:
      return (
        <TextAreaListItem
          updateFieldValueMap={updateFieldValueMap}
          key={field.id}
          field={field}
          fieldValue={fieldValue}
          validationErrors={validationErrors}
          isWrapUp={isWrapUp}
        />
      )
    default:
      return <div className="text-red-500">Error: did you forget to handle this type?</div>
  }
}

type submitReviewArgs = {
  formId: number
  fieldValues: FieldValues
  userDetails: UserDTO
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  setSubmissionGeneralErrorMessage: React.Dispatch<React.SetStateAction<string>>
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>
  submitCallback: (submissionId: number) => void
  reviewType: ReviewType
  hospitalOrAgencyId?: string
  formStartTime: number | null
  stepStartTime: number | null
  currentStepIndex: number
  totalPages: number
  pages: FormPageDTO[]
  setFormCompleted: (completed: boolean) => void
  showSuccessToast: (message: string) => void
}
const submitReview = async ({
  formId,
  fieldValues,
  userDetails,
  setIsSubmitting,
  setSubmissionGeneralErrorMessage,
  setValidationErrors,
  submitCallback,
  reviewType,
  hospitalOrAgencyId,
  formStartTime,
  stepStartTime,
  currentStepIndex,
  totalPages,
  pages,
  setFormCompleted,
  showSuccessToast,
}: submitReviewArgs) => {
  setIsSubmitting(true)

  setValidationErrors({}) // reset errors

  // loop over the fieldValues and create an array of answers { formFieldId, value }
  const answers: FormServiceAddSubmissionAnswerEvent[] = []
  Object.entries(fieldValues).forEach(([fieldId, value]) => {
    answers.push({
      formFieldId: Number(fieldId),
      value: String(value),
    })
  })

  // Calculate optional field completion metrics
  let totalOptionalFields = 0
  let completedOptionalFields = 0
  const optionalFieldDetails: Array<{ field_id: number; field_label: string; completed: boolean }> = []

  pages.forEach((page) => {
    page.fields?.forEach((field) => {
      // Only count visible, non-section fields that are not required
      if (field.visible && !field.isRequired && field.type !== FormFieldTypes.section) {
        totalOptionalFields++
        const fieldValue = fieldValues[field.id]
        const isCompleted = fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== ''
        if (isCompleted) {
          completedOptionalFields++
        }
        optionalFieldDetails.push({
          completed: isCompleted,
          field_id: field.id,
          field_label: field.label,
        })
      }
    })
  })

  const optionalFieldCompletionRate =
    totalOptionalFields > 0 ? Math.round((completedOptionalFields / totalOptionalFields) * 100) : undefined

  const formService = new FormService()
  try {
    const submissionData: FormServiceAddSubmissionEvent = {
      answers,
      email: userDetails.email,
      formId,
    }

    // Add hospitalId or agencyId based on review type
    if (hospitalOrAgencyId) {
      if (reviewType === 'hospital') {
        submissionData.hospitalId = Number(hospitalOrAgencyId)
      } else if (reviewType === 'agency') {
        submissionData.agencyId = Number(hospitalOrAgencyId)
      }
    }

    const createdSubmission = await formService.addSubmission(submissionData)

    // Calculate completion time
    const completionTime = formStartTime ? Math.round((Date.now() - formStartTime) / 1000) : undefined
    const timeOnLastStep = stepStartTime ? Math.round((Date.now() - stepStartTime) / 1000) : undefined

    // Mark form as completed BEFORE navigation to prevent abandonment tracking
    // This needs to be set in the parent component, so we'll use a callback pattern
    // For now, we'll set a flag that will be checked in the abandonment handlers

    // Mark form as completed BEFORE navigation to prevent abandonment tracking
    setFormCompleted(true)

    // Track review submission event with completion metrics
    const submittedEvent = {
      agency_id: reviewType === 'agency' && hospitalOrAgencyId ? hospitalOrAgencyId : undefined,
      completed_at_step: currentStepIndex,
      completion_time_seconds: completionTime,
      form_id: formId,
      hospital_id: reviewType === 'hospital' && hospitalOrAgencyId ? hospitalOrAgencyId : undefined,
      optional_field_completion_rate: optionalFieldCompletionRate,
      optional_field_details: optionalFieldDetails,
      optional_fields_completed: completedOptionalFields,
      // Optional field completion metrics
      optional_fields_total: totalOptionalFields,
      review_type: reviewType,
      time_on_last_step_seconds: timeOnLastStep,
      total_pages: totalPages,
    }

    posthog.capture('review_submitted', submittedEvent)

    clearReviewDraft(reviewType, hospitalOrAgencyId)

    showSuccessToast('Review submitted successfully.')
    await waitForReviewSuccessNavDelay()
    submitCallback(createdSubmission.id)
  } catch (error) {
    console.error(error)
    const errorWithDetails = (error as FormSubmissionError)?.data // assertion to support the error details property we add in the api
    if (errorWithDetails?.details?.length) {
      // convert the details array { fieldId, error } to an array with fieldId as the key and error as the value
      const validationErrors: ValidationErrors = errorWithDetails.details.reduce(
        (acc: ValidationErrors, errorDetail) => {
          acc[`field_${errorDetail.fieldId}`] = errorDetail.error
          return acc
        },
        {},
      )
      console.error(validationErrors)
      setValidationErrors(validationErrors)
      setSubmissionGeneralErrorMessage(errorWithDetails?.message || 'Sorry, there was a problem with your request.')
      setIsSubmitting(false)
    } else {
      setSubmissionGeneralErrorMessage('Sorry, there was a problem with your request.')
      setIsSubmitting(false)
    }
  }
}

type ReviewProps = {
  reviewType: ReviewType
}
const Review = ({ reviewType }: ReviewProps): JSX.Element => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth0()
  const canWriteReview = useCanPerformVerifiedAction()
  const deferWriteGate = useShouldDeferWriteGate()
  const [userDetails, setUserDetails] = useState<UserDTO>()
  const [pages, setPages] = useState<FormPageDTO[]>([])
  const [answeredPages, setAnsweredPages] = useState<number[]>([])
  const [formValid, setFormValid] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [reviewPageIndex, setReviewPageIndex] = useState<number>(0)
  const [generalErrorMessage, setGeneralErrorMessage] = useState('')
  const [submissionGeneralErrorMessage, setSubmissionGeneralErrorMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [fieldValues, setFieldValues] = useState<FieldValues>({})
  const [disablePrimaryUnit, setDisablePrimaryUnit] = useState(false)
  const [entityName, setEntityName] = useState<string>('')
  const history = useHistory()
  const location = useLocation()
  const { showSuccessToast } = useAppToast()
  const { id } = useParams<{ id: string }>()
  const reviewHeaderRef = useRef<HTMLDivElement>(null)

  // Track form analytics
  const formStartTimeRef = useRef<number | null>(null)
  const stepStartTimeRef = useRef<number | null>(null)
  const hasStartedFormRef = useRef<boolean>(false)
  const previousPageIndexRef = useRef<number>(0)
  const formCompletedRef = useRef<boolean>(false) // Track if form was successfully submitted
  const skipDraftSaveRef = useRef(true)
  const abandonmentTrackedRef = useRef<boolean>(false) // Prevent duplicate abandonment tracking
  const currentStepInfoRef = useRef<{
    stepIndex: number
    stepName: string
    stepNumber: number | null
    totalPages: number
  }>({
    stepIndex: 0,
    stepName: '',
    stepNumber: null,
    totalPages: 0,
  })

  const formService = new FormService()

  const formId = reviewTypeToFormId[reviewType]

  useEffect(() => {
    skipDraftSaveRef.current = true
  }, [id, reviewType])

  // Helper function to normalize step names consistently
  const getStepName = (pageIndex: number, page: FormPageDTO | undefined): string => {
    if (pageIndex === 0) {
      // Step 0 should always be "Get Started" for consistency
      return page?.name || 'Get Started'
    }
    // For other steps, use the page name or fallback to step_N
    return page?.name || `step_${pageIndex}`
  }

  useEffect(() => {
    // format the user data
    if (isAuthenticated && user) {
      setUserDetails(UserService.formatUser(user))
    }
  }, [isAuthenticated, user])

  // Track form abandonment ONLY when user truly exits (tab close, navigation away)
  // This does NOT fire on step changes - only on actual page/tab exit
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only track abandonment if form was started but NOT completed, and NOT already tracked
      if (
        hasStartedFormRef.current &&
        formStartTimeRef.current &&
        !formCompletedRef.current &&
        !abandonmentTrackedRef.current
      ) {
        abandonmentTrackedRef.current = true // Mark as tracked to prevent duplicates

        const timeSpent = Date.now() - formStartTimeRef.current
        const stepInfo = currentStepInfoRef.current

        // Use sendBeacon for reliable delivery on page unload
        const abandonedEvent = {
          abandoned_at_step: stepInfo.stepIndex,
          abandonment_type: 'browser_close',
          form_id: formId,
          progress_percentage:
            stepInfo.totalPages > 0 ? Math.round((stepInfo.stepIndex / stepInfo.totalPages) * 100) : 0,
          review_type: reviewType,
          step_name: stepInfo.stepName,
          step_number: stepInfo.stepNumber,
          total_pages: stepInfo.totalPages,
          total_time_spent_seconds: Math.round(timeSpent / 1000),
        }

        posthog.capture('review_form_abandoned', abandonedEvent)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Track abandonment when component unmounts (user navigates away via React Router)
    // This only fires when user navigates to a different route, NOT on step changes
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)

      // Track abandonment on unmount if form was started but NOT completed, and NOT already tracked
      // This will fire when user navigates away from the review page entirely
      if (
        hasStartedFormRef.current &&
        formStartTimeRef.current &&
        !formCompletedRef.current &&
        !abandonmentTrackedRef.current
      ) {
        abandonmentTrackedRef.current = true // Mark as tracked to prevent duplicates

        const timeSpent = Date.now() - formStartTimeRef.current
        const stepInfo = currentStepInfoRef.current

        const abandonedEventNav = {
          abandoned_at_step: stepInfo.stepIndex,
          abandonment_type: 'navigation',
          form_id: formId,
          progress_percentage:
            stepInfo.totalPages > 0 ? Math.round((stepInfo.stepIndex / stepInfo.totalPages) * 100) : 0,
          review_type: reviewType,
          step_name: stepInfo.stepName,
          step_number: stepInfo.stepNumber,
          total_pages: stepInfo.totalPages,
          total_time_spent_seconds: Math.round(timeSpent / 1000),
        }

        posthog.capture('review_form_abandoned', abandonedEventNav)
      }
    }
  }, [reviewType, formId]) // Removed reviewPageIndex and pages from dependencies - this effect should NOT re-run on step changes

  useEffect(() => {
    if (!canWriteReview) {
      return
    }
    setIsLoading(true)

    if (userDetails) {
      formService
        .get({ id: formId, relations: [FormRelations.pages, FormRelations.pagesFields] })
        .then((result) => {
          // Check if pages have fields
          if (!result.pages || result.pages.length === 0) {
            console.error('No pages returned from API')
            setGeneralErrorMessage('No form pages found. Please contact support.')
            setIsLoading(false)
            return
          }

          const pagesWithFields = result.pages.filter((page) => page.fields && page.fields.length > 0)
          if (pagesWithFields.length === 0) {
            console.error('No fields found in any pages. API Response:', result)
            console.error(
              'Pages data:',
              result.pages.map((p) => ({ fieldCount: p.fields?.length || 0, name: p.name })),
            )
            setGeneralErrorMessage(
              'No form fields found. The form may not be configured correctly. Please contact support.',
            )
            setIsLoading(false)
            return
          }

          // before we filter out the hidden fields, set default values for all the fields
          result.pages?.forEach((page) => {
            page.fields?.forEach((field) => {
              if (field.defaultValue) {
                let fieldDefault = ''
                if (field.defaultValue === '{user:position_title}') {
                  if (field.choices?.length) {
                    const choice = field.choices.find((choice) => choice.value === userDetails.position_title)
                    if (choice) {
                      fieldDefault = choice.value
                    } else {
                      fieldDefault = field.choices[0].value
                    }
                  } else {
                    fieldDefault = userDetails.position_title
                  }
                } else if (field.defaultValue === '{user:nursing_specialization}') {
                  if (field.choices?.length) {
                    const choice = field.choices.find((choice) => choice.value === userDetails.nursing_specialization)
                    if (choice) {
                      fieldDefault = choice.value
                    } else {
                      fieldDefault = field.choices[0].value
                    }
                  } else {
                    fieldDefault = userDetails.nursing_specialization
                  }
                } else if (id && (field.inputName === 'a' || field.inputName === 'h')) {
                  fieldDefault = id
                }
                setFieldValues((prev) => {
                  return {
                    ...prev,
                    [String(field.id)]: String(fieldDefault),
                  }
                })
              }
            })
          })

          // loop over the pages and return only fields that are not hidden
          const pagesWithVisibleFields = result.pages?.map((page) => {
            return {
              ...page,
              fields: page.fields?.filter((field) => field.visible === true),
            }
          })

          // loop over the pages and then loop over the fields
          // if any are inputName 'h' or 'a', refetch the field details directly in order to obtain the hospital or agency choices
          // then update the field with the choices
          const promises: Promise<void>[] = []
          pagesWithVisibleFields?.forEach((page) => {
            page.fields?.forEach((field) => {
              if (field.inputName === 'h' || field.inputName === 'a') {
                promises.push(
                  new Promise((resolve, reject) => {
                    if (id) {
                      // an id was specified in the url so we only need to fetch the details of the hospital or agency based on the id
                      if (reviewType === 'hospital') {
                        const hospitalService = new HospitalService()
                        hospitalService
                          .searchOne({ id: Number(id) })
                          .then((hospitalResult) => {
                            field.choices = [{ label: hospitalResult.name, value: String(hospitalResult.id) }]
                            setEntityName(hospitalResult.name)
                            resolve()
                          })
                          .catch((error) => {
                            console.error(error)
                            reject()
                          })
                      } else if (reviewType === 'agency') {
                        const agencyService = new AgencyService()
                        agencyService
                          .searchOne({ id: Number(id) })
                          .then((agencyResult) => {
                            field.choices = [{ label: agencyResult.name, value: String(agencyResult.id) }]
                            setEntityName(agencyResult.name)
                            resolve()
                          })
                          .catch((error) => {
                            console.error(error)
                            reject()
                          })
                      }
                    } else {
                      // no id was specified in the url so we need to fetch all the hospitals or agencies
                      formService
                        .getField({ id: field.id, relations: [] })
                        .then((fieldResult) => {
                          field.choices = fieldResult.choices
                          resolve()
                        })
                        .catch((error) => {
                          console.error(error)
                          reject()
                        })
                    }
                  }),
                )
              }
            })
          })
          Promise.all(promises)
            .then(() => {
              const loadedPages = pagesWithVisibleFields || []
              setPages(loadedPages)

              const draft = loadReviewDraft(reviewType, id)
              if (draft) {
                const maxPageIndex = Math.max(loadedPages.length - 1, 0)
                const restoredPageIndex = Math.min(Math.max(draft.reviewPageIndex, 0), maxPageIndex)

                setFieldValues((prev) => ({ ...prev, ...draft.fieldValues }))
                setReviewPageIndex(restoredPageIndex)
                setAnsweredPages(draft.answeredPages)
                setDisablePrimaryUnit(draft.disablePrimaryUnit)

                if (restoredPageIndex > 0) {
                  hasStartedFormRef.current = true
                  formStartTimeRef.current = Date.now()
                  stepStartTimeRef.current = Date.now()
                  previousPageIndexRef.current = restoredPageIndex
                }

                const restoredPage = loadedPages[restoredPageIndex]
                currentStepInfoRef.current = {
                  stepIndex: restoredPageIndex,
                  stepName: getStepName(restoredPageIndex, restoredPage),
                  stepNumber: restoredPage ? restoredPageIndex + 1 : null,
                  totalPages: loadedPages.length,
                }
              } else if (loadedPages.length > 0) {
                const firstPage = loadedPages[0]
                const initialStepName = getStepName(0, firstPage)
                currentStepInfoRef.current = {
                  stepIndex: 0,
                  stepName: initialStepName,
                  stepNumber: 1,
                  totalPages: loadedPages.length,
                }
              }

              skipDraftSaveRef.current = false
              setIsLoading(false)
            })
            .catch((error) => {
              console.error(error)
              setGeneralErrorMessage('Sorry, there was a problem with your request.')
              setIsLoading(false)
            })
        })
        .catch((error) => {
          console.error(error)
          setGeneralErrorMessage('Sorry, there was a problem with your request.')
          setIsLoading(false)
        })
    }
  }, [canWriteReview, userDetails])

  useEffect(() => {
    if (skipDraftSaveRef.current || !id || isLoading || !pages.length || formCompletedRef.current) {
      return
    }

    const draft = {
      answeredPages,
      disablePrimaryUnit,
      fieldValues,
      reviewPageIndex,
    }

    if (!hasReviewDraftContent(draft)) {
      clearReviewDraft(reviewType, id)
      return
    }

    saveReviewDraft(reviewType, id, draft)
  }, [answeredPages, disablePrimaryUnit, fieldValues, id, isLoading, pages.length, reviewPageIndex, reviewType])

  useEffect(() => {
    validateFields()
  }, [fieldValues, isLoading])

  useEffect(() => {
    if (pages.length) {
      pages.forEach((page, pageIndex) => {
        let hasError = false
        page.fields?.map((field) => {
          if (validationErrors[`field_${field.id}`]) {
            hasError = true
          }
        })
        if (hasError) {
          setReviewPageIndex(pageIndex)
          // scroll to the first error on this page
          const validationElements = document.getElementsByClassName('validation-error')
          if (validationElements.length > 0) {
            const firstValidationElement = validationElements[0]
            ;(firstValidationElement as HTMLElement)?.focus()
          }
          return // found first error so exit loop
        }
      })
    }
  }, [validationErrors])

  // Track step changes and time spent
  useEffect(() => {
    if (hasStartedFormRef.current && pages.length > 0) {
      const currentPage = pages[reviewPageIndex]
      const previousPageIndex = previousPageIndexRef.current

      // Update current step info ref for abandonment tracking
      // Use normalized step name for consistency
      const normalizedStepName = getStepName(reviewPageIndex, currentPage)
      currentStepInfoRef.current = {
        stepIndex: reviewPageIndex,
        stepName: normalizedStepName,
        stepNumber: currentPage ? reviewPageIndex + 1 : null,
        totalPages: pages.length,
      }

      // Track step change - fire on EVERY step change (forward or backward)
      if (previousPageIndex !== reviewPageIndex) {
        // Calculate time spent on previous step (if we have a start time)
        const timeSpentOnStep = stepStartTimeRef.current ? Date.now() - stepStartTimeRef.current : 0
        const previousPage = pages[previousPageIndex]

        // Always fire step change event - PostHog will use the same distinct_id automatically
        // Use consistent 1-based step_number for both events
        // Use normalized step names for consistency with abandonment tracking
        const fromStepName = getStepName(previousPageIndex, previousPage)
        const toStepName = getStepName(reviewPageIndex, currentPage)

        // Calculate step numbers (1-based) - always set if we have valid indices
        const fromStepNumber = previousPageIndex >= 0 && previousPageIndex < pages.length ? previousPageIndex + 1 : null
        const toStepNumber = reviewPageIndex >= 0 && reviewPageIndex < pages.length ? reviewPageIndex + 1 : null

        const stepChangedEvent = {
          form_id: formId,
          from_step: previousPageIndex, // 0-based index
          from_step_name: fromStepName,
          from_step_number: fromStepNumber, // 1-based for consistency
          review_type: reviewType,
          time_spent_on_previous_step_seconds: Math.round(timeSpentOnStep / 1000),
          to_step: reviewPageIndex, // 0-based index
          to_step_name: toStepName,
          to_step_number: toStepNumber, // 1-based for consistency
          total_pages: pages.length,
        }

        posthog.capture('review_form_step_changed', stepChangedEvent)

        // Start tracking time for new step
        stepStartTimeRef.current = Date.now()
        previousPageIndexRef.current = reviewPageIndex
      }
    }
  }, [reviewPageIndex, pages, reviewType, formId])

  // Removed auto-scroll to top on page change to keep screen position
  // useEffect(() => {
  //   if (pages.length) scrollToReviewHeader()
  // }, [reviewPageIndex])

  const submitCallback = (submissionId: number) => {
    if (!id) {
      return
    }
    history.push(getEntityDetailPathFromReviewType(reviewType, id), { scrollToReviewId: submissionId })
  }

  // const scrollToReviewHeader = () => {
  //   if (reviewHeaderRef && reviewHeaderRef.current) scrollIntoView(reviewHeaderRef.current, 0)
  // }

  const validateFields = () => {
    if (pages.length) {
      const nextValidPages: Record<string, boolean> = {}
      const nextAnsweredPages = [...answeredPages]

      pages.forEach((page, pageIndex) => {
        let answeredAll = true
        nextValidPages[`page_${page.id}`] = true
        page.fields?.forEach((field) => {
          if (![FormFieldTypes.section].includes(field.type)) {
            if (typeof fieldValues[field.id] === 'undefined') {
              // hidden fields are not validated
              if (field.visible && field.isRequired) {
                nextValidPages[`page_${page.id}`] = false
              }
              answeredAll = false
            }
          }
        })
        if (answeredAll && !nextAnsweredPages.includes(pageIndex)) {
          nextAnsweredPages.push(pageIndex)
        }
      })

      setAnsweredPages(nextAnsweredPages)
      setFormValid(Object.values(nextValidPages).every((v) => v))
    }
  }

  const updateFieldValueMap = useCallback<UpdateFieldValueMap>((fieldID, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [String(fieldID)]: String(value),
    }))
  }, [])

  const markFormStarted = () => {
    if (hasStartedFormRef.current) {
      return
    }

    hasStartedFormRef.current = true
    formStartTimeRef.current = Date.now()
    stepStartTimeRef.current = Date.now()
    previousPageIndexRef.current = 0
    formCompletedRef.current = false
    abandonmentTrackedRef.current = false

    posthog.capture('review_form_started', {
      agency_id: reviewType === 'agency' && id ? id : undefined,
      form_id: formId,
      hospital_id: reviewType === 'hospital' && id ? id : undefined,
      review_type: reviewType,
      total_pages: pages.length,
    })
  }

  const handleRatingPageAdvance = (page: FormPageDTO, pageIndex: number, fieldId: number, value: number) => {
    const nextFieldValues = { ...fieldValues, [String(fieldId)]: String(value) }
    if (!isReviewQuestionPageComplete(page, nextFieldValues)) {
      return
    }

    markFormStarted()
    setReviewPageIndex(pageIndex + 1)
  }

  const canStartReview = pages[0] ? isGetStartedPageComplete(pages[0], fieldValues) : true

  return (
    <div id="page-root" className="h-screen overflow-auto relative bg-white">
      <TopLogo />

      {!isAuthLoading && !canWriteReview && !deferWriteGate && (
        <AuthPromptModal returnTo={location.pathname} mode={isAuthenticated ? 'verify-email' : 'auth'} />
      )}

      {canWriteReview && userDetails && (
        <div className="px-4 max-w-4xl mx-auto py-8 relative">
          <div ref={reviewHeaderRef}>
            <ReviewStepHeader compact={reviewPageIndex > 0} entityName={entityName} reviewType={reviewType} />
          </div>

          {reviewPageIndex > 0 && pages.length > 0 && !isSubmitting && (
            <ReviewProgressBar currentStep={reviewPageIndex + 1} totalSteps={pages.length} />
          )}

          {generalErrorMessage.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center">
              <span dangerouslySetInnerHTML={{ __html: generalErrorMessage }}></span>
            </div>
          )}

          {(isLoading || isSubmitting) && !(generalErrorMessage.length > 0) && (
            <div className="flex min-h-[400px] flex-col items-center justify-center py-20">
              <Spinner size="lg" />
              {isSubmitting && <p className="mt-4 text-sm font-medium text-gray-600">Submitting your review…</p>}
            </div>
          )}

          {!isLoading && !(generalErrorMessage.length > 0) && (
            <div className={`${isSubmitting ? 'hidden' : ''}`}>
              {/* Getting Started Page */}
              {pages?.[0] && (
                <>
                  <div
                    className={`review-getting-started-page bg-white rounded-2xl shadow-lg p-6 md:p-8 ${reviewPageIndex === 0 ? '' : 'hidden'}`}
                  >
                    {pages[0].fields?.some((field) => field.type !== 'section') ? (
                      <>
                        <ReviewGetStartedIntro reviewType={reviewType} showRoleGuidance />
                        <div className="space-y-5">
                          {pages[0].fields
                            ?.filter((field) => field.type !== 'section')
                            .map((field) =>
                              NodeListItem(
                                field,
                                updateFieldValueMap,
                                userDetails,
                                validationErrors,
                                disablePrimaryUnit,
                                setDisablePrimaryUnit,
                                {
                                  compactLabel: true,
                                  fieldValue: fieldValues[field.id],
                                },
                              ),
                            )}
                        </div>
                      </>
                    ) : (
                      <ReviewGetStartedIntro reviewType={reviewType} />
                    )}

                    <ReviewGetStartedAssurances
                      reviewType={reviewType}
                      showRoleAssurance={Boolean(
                        pages[0].fields?.some((field) => field.visible && field.type === 'select'),
                      )}
                    />

                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        disabled={!canStartReview}
                        className={`w-full max-w-md rounded-xl px-8 py-3.5 text-lg font-semibold text-white shadow-md transition-all ${
                          canStartReview
                            ? 'bg-orange hover:bg-opacity-90'
                            : 'cursor-not-allowed bg-orange/40 text-white/80'
                        }`}
                        onClick={() => {
                          if (!canStartReview) return
                          markFormStarted()
                          setReviewPageIndex(reviewPageIndex + 1)
                        }}
                      >
                        Start Anonymous Review
                      </button>
                    </div>
                  </div>
                  {reviewPageIndex === 0 && <ReviewGetStartedDisclaimer />}
                </>
              )}
              {/* Rating Pages */}
              {pages.map((page, pageIndex) => {
                if (pageIndex !== 0 && pageIndex !== pages.length - 1 && page.fields?.length) {
                  return (
                    <div
                      key={page.id}
                      className={`review-ratings-page bg-white rounded-2xl p-6 md:p-8 ${
                        reviewPageIndex === pageIndex ? '' : 'hidden'
                      }`}
                    >
                      <div className="space-y-4">
                        {page.fields.map((field) =>
                          NodeListItem(
                            field,
                            updateFieldValueMap,
                            userDetails,
                            validationErrors,
                            disablePrimaryUnit,
                            setDisablePrimaryUnit,
                            {
                              fieldValue: fieldValues[field.id],
                              isRatingPage: true,
                              onRatingSelected: (fieldId, value) =>
                                handleRatingPageAdvance(page, pageIndex, fieldId, value),
                            },
                          ),
                        )}
                      </div>
                      <div className="mt-10">
                        <button
                          type="button"
                          className="mx-auto block w-full max-w-xs rounded-xl bg-gray-100 py-3.5 font-semibold text-graphite-grey transition-all hover:bg-gray-200"
                          onClick={() => {
                            markFormStarted()
                            setReviewPageIndex(reviewPageIndex - 1)
                          }}
                        >
                          Previous
                        </button>
                      </div>
                    </div>
                  )
                }
              })}
              {/* Wrapup Page */}
              {pages[pages.length - 1] && (
                <div
                  className={`review-wrapup-page bg-white rounded-2xl p-6 md:p-8 ${
                    reviewPageIndex === pages.length - 1 ? '' : 'hidden'
                  }`}
                >
                  {pages[pages.length - 1].fields
                    ?.filter((field) => isWrapUpVisibleField(field) && isWouldWorkAgainField(field))
                    .map((field) =>
                      NodeListItem(
                        field,
                        updateFieldValueMap,
                        userDetails,
                        validationErrors,
                        disablePrimaryUnit,
                        setDisablePrimaryUnit,
                        { fieldValue: fieldValues[field.id], isWrapUp: true },
                      ),
                    )}
                  <ReviewWrapUpIntro />
                  <div className="space-y-6">
                    {pages[pages.length - 1].fields
                      ?.filter((field) => isWrapUpVisibleField(field) && !isWouldWorkAgainField(field))
                      .map((field) =>
                        NodeListItem(
                          field,
                          updateFieldValueMap,
                          userDetails,
                          validationErrors,
                          disablePrimaryUnit,
                          setDisablePrimaryUnit,
                          { fieldValue: fieldValues[field.id], isWrapUp: true },
                        ),
                      )}
                  </div>
                  {submissionGeneralErrorMessage.length > 0 && (
                    <div
                      className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center"
                      dangerouslySetInnerHTML={{ __html: submissionGeneralErrorMessage }}
                    ></div>
                  )}
                  {!formValid && (
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-center">
                      Please complete your review before submitting.
                    </div>
                  )}
                  <div className="mt-8">
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        disabled={isSubmitting}
                        className={`order-2 rounded-xl bg-gray-100 px-4 py-3.5 text-base font-semibold text-graphite-grey transition-all sm:order-1 sm:flex-1 ${
                          isSubmitting ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-200'
                        }`}
                        onClick={() => {
                          if (isSubmitting) return
                          setReviewPageIndex(reviewPageIndex - 1)
                        }}
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={!formValid || isSubmitting}
                        className={`order-1 min-h-[3.25rem] rounded-xl px-4 py-3.5 text-base font-semibold leading-snug transition-all sm:order-2 sm:flex-1 ${
                          formValid && !isSubmitting
                            ? 'bg-orange text-white shadow-md hover:bg-opacity-90'
                            : 'cursor-not-allowed bg-orange/40 text-white/80'
                        }`}
                        onClick={() => {
                          if (!formValid || isSubmitting) return
                          submitReview({
                            currentStepIndex: reviewPageIndex,
                            fieldValues,
                            formId,
                            formStartTime: formStartTimeRef.current,
                            hospitalOrAgencyId: id,
                            pages,
                            reviewType,
                            setFormCompleted: (completed: boolean) => {
                              formCompletedRef.current = completed
                            },
                            setIsSubmitting,
                            setSubmissionGeneralErrorMessage,
                            setValidationErrors,
                            showSuccessToast,
                            stepStartTime: stepStartTimeRef.current,
                            submitCallback,
                            totalPages: pages.length,
                            userDetails,
                          })
                        }}
                      >
                        <span className="inline text-sm leading-tight sm:hidden">
                          Submit Anonymous
                          <br />
                          Review
                        </span>
                        <span className="hidden sm:inline">Submit Anonymous Review</span>
                      </button>
                    </div>
                    <ReviewSubmitAnonymityReminder />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Review
